import polyline from "@mapbox/polyline";
import { 
  TripResult, 
  OrsRouteRaw,
  Route,
  WayCategory,
  decodeWayCategorySummary 
} from "../types/trip.types";
import { Coordinates } from "../types/distance.types";
import { showMapWarning } from "@/components/warnings/MapWarning";
import { handleApiError } from "@/lib/handleError";
import { showPopup } from "@/components/warnings/Popup";
import { convertSeconds } from "@/lib/duration";

function safeReturnOrsRoute(data: OrsRouteRaw): OrsRouteRaw | undefined {
  const route = data.routes?.[0];
  if (!route || !route.summary || !route.geometry || !route.extras?.waycategory?.summary) {
    showPopup(
      "Unexpected Error", 
      "An unexpected error has occurred."
    );
    return;
  }
  return data;
}

export function parseTripResults(data: OrsRouteRaw): TripResult | undefined {
  const validData = safeReturnOrsRoute(data);
  if (!validData) return;

  const routeData = validData.routes[0];
  
  const routeCoordinates: Route = polyline.decode(routeData.geometry).map(
    ([lat, lon]: [number, number]) => ({ latitude: lat, longitude: lon })
  );

  const decodedWayCategory = decodeWayCategorySummary(routeData.extras!.waycategory!.summary);

  const wayCategory: WayCategory = {
    summary: decodedWayCategory,
    values: routeData.extras!.waycategory!.values ?? []
  };

  const { hours, minutes } = convertSeconds(routeData.summary.duration);

  return {
    distance_km: routeData.summary.distance / 1000,
    hours,
    minutes,
    route: routeCoordinates,
    wayCategory
  };
}

export const fetchTripResults = async(
    locations: Coordinates[],
    radius: number,
): Promise<TripResult | null> => {
  const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/route`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        locations,
        radius,
        options: {
          extra_info: ["waycategory"]
        }
      }),
  });

  if (res.status === 500) { 
    const data = await res.json();
    if (typeof data.error === "string" && data.error.includes("ORS snap request failed")){
      showMapWarning(`⚠ ${data.error}`)
      return null;
    }
  }

  const errorResult = await handleApiError(res);
  if (errorResult === null) return null;

  const data: OrsRouteRaw = await res.json();
  return parseTripResults(data) ?? null;

};

